import { useState } from "react";
import { Button, Checkbox, Input, Space, Typography } from "antd";
import { internationalizeNumericValue, parseInternationalizedValue } from "../utils";
import { EditOutlined } from "@ant-design/icons";
import useGetModulesData from "@/hooks/useGetModulesData";


type HandleFieldChange = (
    clusterId: string,
    collectionId: string,
    field: string,
    value: any,
    record?: any
) => void;

interface IBaseBidProps {
    value: number | null | undefined | string;
    record: any;
    handleFieldChange: HandleFieldChange;
    isSelected: boolean;
}
interface IExactMatchBidInputProps extends IBaseBidProps {
    fieldName: string;
    currencySymbol: string;
    width?: number;
    bidRangePosition?: "side" | "bottom";
}

interface IBidCellRendererProps extends Omit<IBaseBidProps, "value"> {
    value: string;
    fieldName: string;
}

interface ISmartMatchBidCellProps extends Omit<IBaseBidProps, "value"> {
    value: string;
    currencySymbol: string;
}

// BidCellRenderer component moved outside to prevent recreation on each render
export const BidCellRenderer: React.FC<IBidCellRendererProps> = ({
    value,
    record,
    fieldName,
    handleFieldChange,
    isSelected,
}) => {
    // Store enabled state in the record itself to persist across re-renders
    // Initialize useState from record if it exists, otherwise default to false
    // This ensures state persists across re-renders
    const enabledKey = `${fieldName}_enabled`;
    const [isEnabled, setIsEnabled] = useState(
        () => record[enabledKey] ?? false
    );

    const handleCheckboxChange = (checked: boolean) => {
        setIsEnabled(checked);
        // Persist to record so state survives re-renders
        record[enabledKey] = checked;
    };

    return (
        <div className="d-flex align-items-center justify-space-between">
            <Checkbox
                className="me-2"
                disabled={!isSelected}
                checked={isEnabled}
                onChange={(e) => handleCheckboxChange(e.target.checked)}
                style={{
                    width: "16px",
                    height: "16px",
                }}
            />
            <Input
                type="text"
                disabled={!isSelected || !isEnabled}
                value={isEnabled && value ? internationalizeNumericValue(value) : ""}
                placeholder=""
                style={{ width: "100%" }}
                onChange={(e) => {
                    if (isSelected && isEnabled) {
                        const numericValue = parseInternationalizedValue(e.target.value);
                        handleFieldChange(
                            record.clusterId,
                            record.id,
                            fieldName,
                            isNaN(numericValue) ? 0 : numericValue,
                            record
                        );
                    }
                }}
            />
        </div>
    );
};

export const validateBidValue = (
    value: number | null,
    minLimit: number | null | undefined,
    maxLimit: number | null | undefined
): boolean => {
    if (value === null || isNaN(value)) return false;
    return (
        minLimit !== null && minLimit !== undefined && value < minLimit
    );
};

// Helper function to format validation error message
export const formatValidationError = (
    minLimit: number | null | undefined,
    maxLimit: number | null | undefined,
    currencySymbol: string
): string | null => {
    if (minLimit !== null && minLimit !== undefined) {
        return `Min: ${currencySymbol}${minLimit}`;
    }
    return null;
};

export const SuggestedBidRange: React.FC<{
    label: string;
    min: number;
    max: number;
    currencySymbol: string;
    bidRangePosition?: "side" | "bottom";
}> = ({ label, min, max, currencySymbol, bidRangePosition = "bottom" }) => (
    <div
        className={`d-flex align-items-center justify-content-center p-0 m-0 ${bidRangePosition === "side" ? "" : "flex-column"}`}
        style={{ fontSize: "9px", color: "#919191" }}
    >
        <div style={{ textAlign: "center", fontWeight: 600 }}>{label}</div>
        <div style={{ textAlign: "center", fontWeight: 700 }}>
            {` ${currencySymbol} ${min} - ${currencySymbol} ${max}`}
        </div>
    </div>
);

export const ExactMatchBidInput: React.FC<IExactMatchBidInputProps> = ({
    value,
    record,
    fieldName,
    handleFieldChange,
    currencySymbol,
    isSelected,
    width,
    bidRangePosition = "bottom",
}) => {
    const maxLimit = record?.max_bid_limit;
    const minExactMatchBid = useGetModulesData({type: "channel-options"})?.minExactMatchBid;
    const minLimit = record?.min_bid_limit || minExactMatchBid || 0;

    const isValueInvalid =
        value === null
            ? false
            : validateBidValue(Number(value), minLimit, maxLimit);
    const errorMessage = formatValidationError(
        minLimit,
        maxLimit,
        currencySymbol
    );

    return (
        <div className="d-flex flex-column align-items-center justify-content-center">
            <Input
                type="text"
                value={isSelected && value ? internationalizeNumericValue(value) : null}
                disabled={!isSelected}
                placeholder=""
                style={{ width: width || 85 }}
                onChange={(e) => {
                    if (isSelected) {
                        const numericValue = parseInternationalizedValue(e.target.value);
                        handleFieldChange(
                            record.clusterId,
                            record.id,
                            fieldName,
                            isNaN(numericValue) ? 0 : numericValue,
                            record
                        );
                    }
                }}
            />
            {isValueInvalid && errorMessage && (
                <div
                    style={{
                        fontSize: "8px",
                        color: "#ff4d4f",
                        textAlign: "center",
                        marginTop: "2px",
                        fontWeight: 500,
                        lineHeight: "1.2",
                        maxWidth: "85px",
                    }}
                >
                    {errorMessage}
                </div>
            )}
            {
                record.exact_suggested_min && record.exact_suggested_max && (
                    <SuggestedBidRange
                        label="Suggested Bid Range"
                        min={record.exact_suggested_min}
                        max={record.exact_suggested_max}
                        currencySymbol={currencySymbol}
                        bidRangePosition={bidRangePosition}
                    />
                )
            }
        </div>
    );
};

export const SmartMatchBidCell: React.FC<ISmartMatchBidCellProps> = ({
    value,
    record,
    handleFieldChange,
    currencySymbol,
    isSelected,
}) => (
    <div className="d-flex flex-column align-items-end justify-content-center">
        <BidCellRenderer
            value={value}
            record={record}
            fieldName="smart_match_bid"
            handleFieldChange={handleFieldChange}
            isSelected={isSelected}
        />
        {
            record.smart_suggested_min && record.smart_suggested_max && (
                <SuggestedBidRange
                    label="Bid Range"
                    min={record.smart_suggested_min}
                    max={record.smart_suggested_max}
                    currencySymbol={currencySymbol}
                />
            )
        }
    </div>
);

export const createCompactMultiSelectTagRenderer = (
    currentValue: any[],
    options: Array<{ value: string; label: string }>
) => {
    return (props: any) => {
        // Only render content for the first tag, hide all others
        const allValues = currentValue || [];
        if (props.value !== allValues[0]) return null;

        // Get the first selection's label
        const firstLabel =
            options.find((opt) => opt.value === allValues[0])?.label ||
            allValues[0];
        const remainingCount = allValues.length - 1;

        // Display as "First Selection +count" if more than one selected
        const displayText =
            remainingCount > 0
                ? `${firstLabel} +${remainingCount}`
                : firstLabel;

        // Return a span with inline styles to remove tag appearance and add padding
        return (
            <span
                style={{
                    background: "transparent",
                    border: "none",
                    padding: "0px 16px",
                    margin: 0,
                    display: "inline-block",
                }}
            >
                {displayText}
            </span>
        );
    };
};

export const EditableCampaignNameCell: React.FC<{
    value: string;
    record: any;
    handleCampaignNameChange?: (clusterId: string, collectionId: string, campaignId: string, newName: string) => void;
}> = ({ value, record, handleCampaignNameChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    
    const handleSave = () => {
        if (handleCampaignNameChange && editValue.trim()) {
            handleCampaignNameChange(
                record.clusterId,
                record.collectionId,
                record.id,
                editValue.trim()
            );
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    return (
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                    <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onPressEnter={handleSave}
                        onBlur={handleCancel}
                        autoFocus
                        style={{ width: '100%', fontWeight: 500, fontSize: '14px' }}
                    />
                ) : (
                    <Typography.Text style={{ fontWeight: 500, fontSize: '14px' }}>{value || 'N/A'}</Typography.Text>
                )}
            </div>
            {handleCampaignNameChange && !isEditing && (
                <Button
                    icon={<EditOutlined />}
                    style={{ 
                        display: 'flex',
                        width: '35px',
                        height: '35px',
                        padding: '8px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                        border: '1px solid #CBD9E7',
                        flexShrink: 0
                    }}
                    onClick={() => setIsEditing(true)}
                />
            )}
        </div>
    );
};